import { mapDTO } from '@twistezo/ts-dto-mapper'
import _ from 'lodash'

import { AbTestsVariantsSchemaDTO } from 'features/ab-tests/api/types/abTestsVariantsSchemaDTO'
import {
  AbTestVariantsSchema,
  BidderAttributeItem,
  PrebidModuleItemAttribute,
} from 'features/ab-tests/types/abTestVariantsSchema'
import { DomainBidderPriority } from 'features/globalSetup/types/bidderPriority'
import { DomainBidder } from 'features/globalSetup/types/domainBidder'
import { PrebidModule } from 'features/globalSetup/types/prebidModule'
import { millisecondsToSeconds } from 'utils/number'
import {
  domainBidderPriorityOptions,
  lazyLoadTypeOptions,
  prebidBehavioursOptions,
} from './options'
import { MetaPrebidBidderItem, MetaPrebidModule, MetaPrebidModuleItem } from './types'
import { parsePrimitiveValue, traverseMapAttributes } from './utils'

type From = AbTestsVariantsSchemaDTO
type To = AbTestVariantsSchema

export const fromDTO = (from: From): To =>
  mapDTO<From, To>({ from }).transform((schemaDTO: From): To => {
    const {
      general: generalDTO,
      prebidStack: prebidStackDTO,
      refresher: refresherDTO,
      reportedKeyValue,
      trafficShare: trafficShareDTO,
      unfilledRecovery: unfilledRecoveryDTO,
      viewabilityTools: viewabilityToolsDTO,
    } = traverseMapAttributes<From, To>(schemaDTO)

    const trafficShare: AbTestVariantsSchema['trafficShare'] = {
      ...trafficShareDTO,
      value: trafficShareDTO.value ? trafficShareDTO.value : 0,
    }

    const general = {
      ...generalDTO,
    }

    const prebidStack = {
      ...prebidStackDTO,
      bidders: {
        label: prebidStackDTO.bidders.label,
        ...regroupBiddersItems(prebidStackDTO.bidders.items as unknown as MetaPrebidBidderItem[]),
      },
      prebidModules: {
        ...prebidStackDTO.prebidModules,
        analytics: {
          label: prebidStackDTO.prebidModules.analytics.label,
          ...regroupPrebidModuleItems(
            prebidStackDTO.prebidModules.analytics as unknown as MetaPrebidModule,
          ),
        },
        general: {
          label: prebidStackDTO.prebidModules.general.label,
          ...regroupPrebidModuleItems(
            prebidStackDTO.prebidModules.general as unknown as MetaPrebidModule,
          ),
        },
        userId: {
          label: prebidStackDTO.prebidModules.userId.label,
          ...regroupPrebidModuleItems(
            prebidStackDTO.prebidModules.userId as unknown as MetaPrebidModule,
          ),
        },
        vendorSpecific: {
          label: prebidStackDTO.prebidModules.vendorSpecific.label,
          ...regroupPrebidModuleItems(
            prebidStackDTO.prebidModules.vendorSpecific as unknown as MetaPrebidModule,
          ),
        },
      },
    }

    const refresher = {
      ...refresherDTO,
      basicSettings: {
        ...refresherDTO.basicSettings,
        desktopRefreshInterval: {
          ...refresherDTO.basicSettings.desktopRefreshInterval,
          value: refresherDTO.basicSettings.desktopRefreshInterval.value
            ? millisecondsToSeconds(
                refresherDTO.basicSettings.desktopRefreshInterval.value as number,
              )
            : undefined,
        },
        mobileRefreshInterval: {
          ...refresherDTO.basicSettings.mobileRefreshInterval,
          value: refresherDTO.basicSettings.mobileRefreshInterval.value
            ? millisecondsToSeconds(
                refresherDTO.basicSettings.mobileRefreshInterval.value as number,
              )
            : undefined,
        },
      },
    }

    const unfilledRecovery = {
      ...unfilledRecoveryDTO,
      prebidBehaviourType: {
        ...unfilledRecoveryDTO.prebidBehaviourType,
        options: prebidBehavioursOptions(),
        value: parsePrimitiveValue(
          unfilledRecoveryDTO.prebidBehaviourType.value,
          unfilledRecoveryDTO.prebidBehaviourType.type,
        ),
      },
    }

    const viewabilityTools = {
      ...viewabilityToolsDTO,
      lazyLoading: {
        ...viewabilityToolsDTO.lazyLoading,
        lazyLoadType: {
          ...viewabilityToolsDTO.lazyLoading.lazyLoadType,
          options: lazyLoadTypeOptions(),
          value: parsePrimitiveValue(
            viewabilityToolsDTO.lazyLoading.lazyLoadType.value,
            viewabilityToolsDTO.lazyLoading.lazyLoadType.type,
          ),
        },
      },
    }

    return {
      general,
      // FIXME: @typescript-eslint/no-explicit-any
      prebidStack: prebidStack as any,
      refresher,
      reportedKeyValue,
      trafficShare,
      unfilledRecovery,
      viewabilityTools,
    }
  })

export const regroupPrebidModuleItems = (
  module: MetaPrebidModule,
): Record<PrebidModule['id'], PrebidModuleItemAttribute> =>
  _.chain(module.items)
    .keyBy('id')
    .mapValues(
      ({ enabled: { label, type, value } }: MetaPrebidModuleItem): PrebidModuleItemAttribute => ({
        enabled: {
          label,
          type,
          value: parsePrimitiveValue(value, type),
        },
      }),
    )
    .value()

export const regroupBiddersItems = (
  items: MetaPrebidBidderItem[],
): Record<DomainBidder['id'], BidderAttributeItem> =>
  _.chain(items)
    .keyBy('id')
    .mapValues(
      ({
        csEnabled,
        id,
        priority,
        s2sEnabled,
        validValues,
      }: MetaPrebidBidderItem): BidderAttributeItem => ({
        csEnabled,
        id,
        priority: {
          ...priority,
          options: domainBidderPriorityOptions(validValues as DomainBidderPriority[]),
          value: parsePrimitiveValue(priority.value, priority.type),
        },
        s2sEnabled,
      }),
    )
    .value()
